'use client';

import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import { PlayerAvatar } from '@/components/atoms/PlayerAvatar';
import { TEAM_LEAGUES_PLACEHOLDER_IMAGE } from '@/components/constants/constants';
import { Team } from '@/entities/player-details/PlayerBanner';
import { getPlayerImageUrl, getProfileImageUrl } from '@/utils/utils';

interface PlayerDetailsTeammatesListProps {
	teams: Team[];
	isFreeAgent: boolean;
}

export const PlayerDetailsTeammatesList = ({ teams, isFreeAgent }: PlayerDetailsTeammatesListProps) => {
	const params = useParams<{ teamLeagueId: string }>();

	if (isFreeAgent) return null;

	return (
		<div
			className={clsx('flex w-full md:w-fit overflow-auto scrollbar-thin pl-2', {
				'justify-center md:justify-start': teams.length === 1,
				'justify-start sm:justify-center md:justify-start': teams.length !== 1
			})}
		>
			{teams.map((team) => {
				return (
					<div className="flex flex-col gap-4 xl:px-5 pb-6 md:py-9" key={team.teamUuid}>
						<div className="flex items-center justify-center size-[84px]">
							<img
								src={getCDNURL(team.teamLogo || TEAM_LEAGUES_PLACEHOLDER_IMAGE, 300, 300)}
								alt="Team Logo"
								className="object-fit h-full"
							/>
						</div>

						<div className="flex flex-col gap-3 h-full w-full">
							<span className="text-lg not-italic font-bold leading-7 text-gray-900">Teammates</span>

							<div className="flex gap-3 h-fit">
								<div className="relative">
									<div
										className="absolute left-0 top-0.5 bottom-0 w-0.5"
										style={{
											backgroundImage: `linear-gradient(to bottom, #EF6820, #FFF)`
										}}
									/>
									<div className="absolute left-px top-0 z-20 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-[#EF6820] bg-white" />
								</div>

								<div className="flex flex-col justify-start gap-3 w-full h-[205px] no-scrollbar overflow-auto">
									{team.teammates.map((teammate) => {
										const imageUrl = getPlayerImageUrl(teammate.photo);

										return (
											<Link
												key={teammate.playerUuid}
												prefetch={false}
												href={`/team-leagues/${params.teamLeagueId}/player-details/${teammate.playerUuid}/stats`}
												className="block"
											>
												<div className="flex items-center w-full cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors">
													<div className="flex items-center w-full">
														<div className=" flex-shrink-0 overflow-hidden">
															<PlayerAvatar
																name={[teammate.firstName, teammate.lastNameDisplay].filter(Boolean).join(' ')}
																src={imageUrl ? getProfileImageUrl(imageUrl, 120, 120) : undefined}
																className="size-[60px] border-2 border-[#EF6820] rounded-full"
																textClassName="text-xl bg-gray-50 text-gray-500"
															/>
														</div>
														<div className="ml-4 text-gray-900">
															<p className="text-base lg:text-lg">{teammate.firstName}</p>
															<p className="text-base lg:text-lg font-bold">{teammate.lastNameDisplay}</p>
														</div>
													</div>
												</div>
											</Link>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};
